// NodeMissions.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useBase } from 'context/BaseContext';

// Utilities
import {
  getMissionProgress,
  getMissionRunStatus,
  normalizeMissionRun,
} from 'node/missions/mission/utils/missionRunUtils';
import { resolveSelectedPlaybookEntry } from 'node/missions/mission/utils/playbookRunSelectionUtils';

// Hooks
import useOpenDatabaseSettingsModal from 'collection/hooks/useOpenDatabaseSettingsModal';
import useBaseAccess from 'hooks/useBaseAccess';
import useMergedWorkflowTemplates from 'hooks/useMergedWorkflowTemplates';
import { useMissionBotRunner } from 'node/missions/hooks/useMissionBotRunner';
import { useMissionRunActions } from 'node/missions/hooks/useMissionRunActions';
import useNodeMissionTemplateEditor from 'node/missions/hooks/useNodeMissionTemplateEditor';
import useWorkflowStepNodes from 'node/missions/hooks/useWorkflowStepNodes';

// Constants
import { SETTINGS_TAB_WORKFLOWS } from 'collection/settings/databaseSettingsTabs';

// CSS
import './NodeMissions.css';

// Components
import DatabaseModal from 'collection/settings/DatabaseModal';
import MissionModal from 'collection/settings/missions/MissionModal';
import MissionTaskModal from 'missions/taskmodal/MissionTaskModal';
import NodeMissionsEmpty from 'node/missions/empty/NodeMissionsEmpty';
import NodeMissionsList from 'node/missions/NodeMissionsList';
import NodeMission from 'node/missions/mission/NodeMission';
import NodeMissionPreflightModal from 'node/missions/mission/preflight/NodeMissionPreflightModal';

/**
 * NodeMissions
 *
 * Node Playbook tab: named templates → Start preflight → one selected live run,
 * with a title switcher when more than one playbook can run on the node.
 *
 * @param {object|null} node - Current anchor node.
 * @param {function} onNodeChange - Persists node document patches.
 * @param {object|null} typeDef - Current node type definition.
 * @param {object|null} user - Current signed-in user.
 * @param {string} requestWizardTemplateKey - Template key requested by quick-add.
 * @param {function} onRequestWizardHandled - Clears the pending wizard request.
 * @param {'compact'|'expanded'} addon - Layout density for peek vs full node views.
 * @returns {ReactNode} The rendered Playbook surface.
 */
const NodeMissions = React.memo(({
  node = null,
  onNodeChange,
  typeDef = null,
  user = null,
  requestWizardTemplateKey = '',
  onRequestWizardHandled,
  addon = 'expanded',
}) => {
  const language = useCurrentLanguage();
  const { getLabel, getLabelPlural } = useNodeTypes();
  const { activeBase } = useBase();
  const { can, isRoleLoading } = useBaseAccess();
  const {
    canOpenSettingsForType,
    databaseModalProps,
    isOpening: isOpeningWorkflowSettings,
    openDatabaseSettings,
  } = useOpenDatabaseSettingsModal({ allowSystemTypes: true });

  // Preflight target template key (unstarted playbook confirm)
  const [preflightTemplateKey, setPreflightTemplateKey] = useState('');
  // Per-node session selection for which launched playbook is on screen
  const [sessionSelectedTemplateKey, setSessionSelectedTemplateKey] = useState('');
  // Avoid re-opening single-template preflight after the user dismisses it
  const [didAutoPreflight, setDidAutoPreflight] = useState(false);

  const typeKey = String(node?.type || typeDef?.key || '').trim();
  const baseKey = String(node?.baseKey || activeBase?.key || typeDef?.baseKey || '').trim() || null;
  const nodeKey = String(node?.key || '').trim();

  const missionActions = useMissionRunActions({
    baseKey,
    node,
    onNodeChange,
    user,
  });

  const {
    blockingTaskKey,
    handleLaunchMission,
    savingMissionKey,
    setBlockingTaskKey,
  } = missionActions;
  const isSystemType = typeDef?.system === true || typeDef?.isSystem === true;
  const canManageMissions = !isRoleLoading && can('types.update') && !isSystemType;
  const canOpenWorkflowSettings = Boolean(
    typeKey
    && baseKey
    && canOpenSettingsForType(typeKey, typeDef),
  );
  const isCompact = addon === 'compact';

  const singularTypeLabel = getLabel(typeKey, language, { fallback: 'item' }) || 'item';
  const pluralTypeLabel = getLabelPlural(typeKey, language, { fallback: 'items' }) || 'items';

  const { templates: mergedTemplates } = useMergedWorkflowTemplates({
    typeDef,
    baseKey,
    typeKey,
  });

  const templates = mergedTemplates;

  const missionEntries = useMemo(() => {
    return templates.map((template) => {
      const missionRun = normalizeMissionRun(node?.missionRuns?.[template.key], template);

      return {
        missionProgress: getMissionProgress(missionRun),
        missionRun,
        missionStatus: getMissionRunStatus(missionRun),
        template,
      };
    });
  }, [node?.missionRuns, templates]);

  const hasRuns = useMemo(
    () => missionEntries.some((entry) => Boolean(entry.missionRun)),
    [missionEntries]
  );

  const selectedMissionEntry = useMemo(() => (
    resolveSelectedPlaybookEntry({
      missionEntries,
      sessionSelectedTemplateKey,
      user,
    })
  ), [missionEntries, sessionSelectedTemplateKey, user]);

  const { stepNodesByKey } = useWorkflowStepNodes(selectedMissionEntry?.missionRun);

  const isEmpty = missionEntries.length === 0;
  const showPlaybookSwitcher = templates.length >= 2;

  const templateHasRun = useCallback((templateKey) => {
    const key = String(templateKey || '').trim();
    if (!key) return false;
    return Boolean(node?.missionRuns?.[key]);
  }, [node?.missionRuns]);

  const preflightTemplate = useMemo(() => {
    const templateKey = String(preflightTemplateKey || '').trim();
    if (!templateKey || templateHasRun(templateKey)) return null;
    return templates.find((template) => template.key === templateKey) || null;
  }, [preflightTemplateKey, templateHasRun, templates]);

  const templateEditor = useNodeMissionTemplateEditor({
    enabled: isEmpty && canManageMissions,
    typeDef,
    typeKey,
    baseKey,
  });

  const allMissionTasks = useMemo(() => {
    return missionEntries.flatMap(({ missionRun }) => (
      Array.isArray(missionRun?.tasks) ? missionRun.tasks : []
    ));
  }, [missionEntries]);

  useMissionBotRunner({
    baseKey,
    missionEntries,
    node,
    savingMissionKey,
    typeDef,
  });

  /**
   * handleOpenPreflight
   *
   * Opens Start preflight for a template that does not yet have a run.
   *
   * @param {object|string} templateOrKey - Template object or key.
   */
  const handleOpenPreflight = useCallback((templateOrKey) => {
    const templateKey = typeof templateOrKey === 'string'
      ? String(templateOrKey || '').trim()
      : String(templateOrKey?.key || '').trim();
    if (!templateKey || templateHasRun(templateKey)) return;

    setPreflightTemplateKey(templateKey);
  }, [templateHasRun]);

  /**
   * handleClosePreflight
   *
   * Dismisses Start preflight without creating a run.
   */
  const handleClosePreflight = useCallback(() => {
    setPreflightTemplateKey('');
  }, []);

  /**
   * handleLaunchFromPreflight
   *
   * Creates the run for the preflight template and selects it for this session.
   *
   * @param {object} template - Template being started.
   * @param {object} [options] - Launch overrides from preflight.
   * @returns {Promise<string|null>} Started template key.
   */
  const handleLaunchFromPreflight = useCallback(async (template, options = {}) => {
    const templateKey = String(template?.key || '').trim();
    if (!templateKey || templateHasRun(templateKey)) return null;

    const launchedKey = await handleLaunchMission(template, options);
    if (launchedKey) {
      setSessionSelectedTemplateKey(String(launchedKey).trim());
    }
    return launchedKey;
  }, [handleLaunchMission, templateHasRun]);

  /**
   * handleSelectPlaybook
   *
   * Title switcher: switch to an existing run, or open Start for an unstarted name.
   *
   * @param {string} templateKey - Picker row template key.
   */
  const handleSelectPlaybook = useCallback((templateKey) => {
    const key = String(templateKey || '').trim();
    if (!key) return;

    if (templateHasRun(key)) {
      setSessionSelectedTemplateKey(key);
      setPreflightTemplateKey('');
      return;
    }

    handleOpenPreflight(key);
  }, [handleOpenPreflight, templateHasRun]);

  const handleCreateMission = useCallback(() => {
    templateEditor.handleAddMissionTemplate?.();
  }, [templateEditor]);

  /**
   * Opens collection settings directly into the workflow designer for a template.
   *
   * @param {object|null} template - Workflow template to design.
   * @returns {Promise<void>}
   */
  const handleOpenWorkflowSettings = useCallback(async (template = null) => {
    const templateKey = String(template?.key || '').trim();
    if (!templateKey || !baseKey || !typeKey || isOpeningWorkflowSettings) return;

    const databaseKey = `${baseKey}_${typeKey}_all`.replace(/\//g, '_');

    await openDatabaseSettings({
      databaseKey,
      typeKey,
      baseKey,
      initialTab: SETTINGS_TAB_WORKFLOWS,
      workflowEditorRequest: { templateKey },
    });
  }, [
    baseKey,
    isOpeningWorkflowSettings,
    openDatabaseSettings,
    typeKey,
  ]);

  useEffect(() => {
    setSessionSelectedTemplateKey('');
    setDidAutoPreflight(false);
    setPreflightTemplateKey('');
  }, [nodeKey]);

  useEffect(() => {
    if (!blockingTaskKey) return;

    const hasBlockingTask = allMissionTasks.some((task) => task.key === blockingTaskKey);
    if (!hasBlockingTask) {
      setBlockingTaskKey('');
    }
  }, [allMissionTasks, blockingTaskKey, setBlockingTaskKey]);

  useEffect(() => {
    if (!preflightTemplateKey) return;
    if (
      templateHasRun(preflightTemplateKey)
      || !templates.some((template) => template.key === preflightTemplateKey)
    ) {
      setPreflightTemplateKey('');
    }
  }, [preflightTemplateKey, templateHasRun, templates]);

  // One template + zero runs: open Start immediately (once per node visit).
  useEffect(() => {
    if (hasRuns || templates.length !== 1 || didAutoPreflight || preflightTemplateKey) {
      return;
    }

    const onlyKey = String(templates[0]?.key || '').trim();
    if (!onlyKey) return;

    setDidAutoPreflight(true);
    setPreflightTemplateKey(onlyKey);
  }, [didAutoPreflight, hasRuns, preflightTemplateKey, templates]);

  useEffect(() => {
    if (!requestWizardTemplateKey) return;

    const templateKey = String(requestWizardTemplateKey).trim();
    onRequestWizardHandled?.();

    if (!templateKey) return;

    if (templateHasRun(templateKey)) {
      setSessionSelectedTemplateKey(templateKey);
      return;
    }

    handleOpenPreflight(templateKey);
  }, [
    handleOpenPreflight,
    onRequestWizardHandled,
    requestWizardTemplateKey,
    templateHasRun,
  ]);

  const preflightModal = (
    <NodeMissionPreflightModal
      node={node}
      savingMissionKey={savingMissionKey}
      template={preflightTemplate}
      onClose={handleClosePreflight}
      onLaunch={handleLaunchFromPreflight}
    />
  );

  if (isEmpty) {
    return (
      <>
        <NodeMissionsEmpty
          canManageMissions={canManageMissions}
          isEditorOpen={templateEditor.missionTemplateEditorOpen}
          pluralTypeLabel={pluralTypeLabel}
          singularTypeLabel={singularTypeLabel}
          addon={addon}
          onCreateMission={handleCreateMission}
        />

        {canManageMissions ? (
          <>
            <MissionModal
              canSaveMissionMeta={templateEditor.canSaveMissionMeta}
              isOpen={templateEditor.missionTemplateEditorOpen}
              isSaving={templateEditor.isSaving}
              missionDescription={templateEditor.missionDescription}
              missionMetaError={templateEditor.missionMetaError}
              missionTasks={templateEditor.missionTasks}
              missionTitle={templateEditor.missionTitle}
              mode={templateEditor.missionTemplateEditorMode}
              selectedMissionTemplateKey={templateEditor.selectedMissionTemplateKey}
              setOpen={templateEditor.setMissionTemplateEditorOpen}
              typeLabel={templateEditor.typeLabel}
              onAddMissionTask={templateEditor.handleAddMissionTask}
              onDeleteMissionTemplate={() => templateEditor.handleDeleteMissionTemplate(templateEditor.selectedMissionTemplateKey)}
              onEditMissionTask={templateEditor.handleEditMissionTask}
              onMissionDescriptionChange={templateEditor.setMissionDescription}
              onMissionTitleChange={templateEditor.setMissionTitle}
              onReorderMissionTask={templateEditor.handleReorderMissionTask}
              onSaveMissionMeta={templateEditor.handleSaveMissionMeta}
            />

            <MissionTaskModal
              isOpen={templateEditor.missionEditorOpen}
              setOpen={templateEditor.setMissionEditorOpen}
              mode={templateEditor.editingMissionTask ? 'edit' : 'create'}
              task={templateEditor.editingMissionTask}
              availableTasks={templateEditor.missionTasks}
              availableFields={templateEditor.resolvedProperties}
              edges={templateEditor.missionEdges}
              recordTypeOptions={templateEditor.nodeTypeOptions}
              baseKey={baseKey}
              onDelete={templateEditor.handleDeleteMissionTask}
              onSave={templateEditor.handleSaveMissionTask}
            />
          </>
        ) : null}
      </>
    );
  }

  // One or more runs: show the selected live playbook (wizard/list).
  if (hasRuns && selectedMissionEntry) {
    return (
      <>
        <div className={`node-missions-container${isCompact ? ' node-missions-container-compact' : ''}`}>
          <NodeMission
            key={selectedMissionEntry.template.key}
            compact={isCompact}
            template={selectedMissionEntry.template}
            missionRun={selectedMissionEntry.missionRun}
            missionProgress={selectedMissionEntry.missionProgress}
            missionStatus={selectedMissionEntry.missionStatus}
            stepNodesByKey={stepNodesByKey}
            hostNode={node}
            user={user}
            missionActions={missionActions}
            onHostNodeChange={onNodeChange}
            onOpenWorkflowSettings={
              canOpenWorkflowSettings
                ? () => handleOpenWorkflowSettings(selectedMissionEntry.template)
                : null
            }
            playbookSwitcherEntries={showPlaybookSwitcher ? missionEntries : null}
            selectedTemplateKey={selectedMissionEntry.template.key}
            onSelectPlaybook={showPlaybookSwitcher ? handleSelectPlaybook : null}
          />
        </div>

        {preflightModal}
        <DatabaseModal {...databaseModalProps} />
      </>
    );
  }

  // Zero runs: informative chooser (compact peek + full node share the same surface).
  return (
    <div className={`node-missions-container${isCompact ? ' node-missions-container-compact' : ''}`}>
      <NodeMissionsList
        missionEntries={missionEntries}
        onSelectMission={handleOpenPreflight}
        singularTypeLabel={singularTypeLabel}
      />
      {preflightModal}
    </div>
  );
});

NodeMissions.displayName = 'NodeMissions';

export default NodeMissions;
